import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Phone, MapPin, Calendar, MessageSquare } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { sendEmail, type FormData as EmailFormData } from "@/utils/email";

interface ScheduleMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ScheduleMeetingDialog = ({ open, onOpenChange }: ScheduleMeetingDialogProps) => {
  const { toast } = useToast();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [purpose, setPurpose] = useState('loan-consultation');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    if (!name || !phone || !date || !time) {
      toast({
        title: "❌ Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const purposeText = purpose === 'loan-consultation' ? 'Loan Consultation' :
                      purpose === 'loan-status' ? 'Loan Status Update' : 'General Inquiry';

    // Format date for display
    const formattedDate = new Date(date).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Create message content
    const messageContent = `📅 *Meeting Request* 📅

👤 *Contact Information*
• Name: ${name}
• Phone: ${phone}
• Email: ${email || 'Not provided'}

📅 *Meeting Details*
• Date: ${formattedDate}
• Time: ${time}
• Purpose: ${purposeText}

---
📅 Submitted on: ${new Date().toLocaleString()}
🌐 This is an automated message from the website contact form.`;

    try {
      // 1. Submit to WhatsApp
      window.open(`https://wa.me/919324471736?text=${encodeURIComponent(messageContent)}`, '_blank');

      // 2. Prepare and send email
      const emailData: EmailFormData & Record<string, unknown> = {
        from_name: name,
        from_email: email,
        to_email: import.meta.env.VITE_EMAILJS_BUSINESS_EMAIL || 'whitepearlfinance@gmail.com',
        phone: phone,
        loan_type: purposeText,
        message: messageContent,
        date: formattedDate,
        time: time,
        company_name: 'White Pearl Finance',
        subject: `New Meeting Request from ${name}`,
        to_name: 'Support Team',
        whatsapp_message: messageContent.replace(/\n/g, '<br>')
      };

      // Submit to Email
      console.log('Attempting to send email with data:', emailData);
      const { success, message: emailMessage } = await sendEmail(emailData);
      console.log('Email submission result:', { success, emailMessage });

      // Reset form
      setName('');
      setEmail('');
      setPhone('');
      setDate('');
      setTime('');
      setPurpose('loan-consultation');

      // Close the dialog after successful submission
      onOpenChange(false);

      // Show success message
      if (success) {
        toast({
          title: "✅ Request Submitted!",
          description: "We've received your meeting request and sent a confirmation to your email.",
          duration: 5000
        });
      } else {
        console.error('Email submission failed:', emailMessage);
        toast({
          title: "📅 WhatsApp Opened!",
          description: "We've prepared a WhatsApp message for you. Email confirmation may be delayed.",
          duration: 5000
        });
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "❌ Error",
        description: "There was an error processing your request. Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center mb-2">Schedule a Meeting</DialogTitle>
          <p className="text-sm text-gray-400 text-center mb-6">
            Book a 30-minute consultation with our loan expert
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Your Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Date *</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Time *</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                min="09:00"
                max="19:00"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Phone Number *</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Purpose *</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="loan-consultation">Loan Consultation</SelectItem>
                <SelectItem value="document-submission">Document Submission</SelectItem>
                <SelectItem value="loan-status">Loan Status Update</SelectItem>
                <SelectItem value="other">Other Inquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 mt-4">
            Schedule Meeting
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Contact = () => {
  interface FormData {
    name: string;
    email: string;
    phone: string;
    purpose: string;
    message: string;
    date: string;
    time: string;
    timestamp?: string;
  }

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    purpose: "loan-inquiry",
    message: "",
    date: "",
    time: "",
    timestamp: new Date().toISOString()
  });

  const { toast } = useToast();
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);

  // Form submission handler
  // Handler for WhatsApp meeting scheduling
  const handleScheduleMeeting = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    console.log('Form submitted with data:', formData);

    // Basic validation
    if (!formData.name || !formData.phone || !formData.date || !formData.time) {
      console.log('Validation failed - missing required fields');
      toast({
        title: "❌ Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const purposeText = formData.purpose === 'loan-inquiry' ? 'Loan Inquiry' :
                      formData.purpose === 'account-support' ? 'Account Support' : 'General Question';

    // Format date for display
    const formattedDate = formData.date ? new Date(formData.date).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : '';

    // Create WhatsApp message
    const whatsappMessage = `📅 *Meeting Request* 📅

👤 *Contact Information*
• Name: ${formData.name}
• Email: ${formData.email}
• Phone: ${formData.phone}

📅 *Meeting Details*
• Date: ${formattedDate}
• Time: ${formData.time}
• Purpose: ${purposeText}

I would like to schedule a meeting at the proposed time. Please confirm if this works for you.`;

    console.log('Generated WhatsApp message:', whatsappMessage);

    try {
      // 1. Submit to WhatsApp
      const whatsappUrl = `https://wa.me/919324471736?text=${encodeURIComponent(whatsappMessage)}`;
      console.log('Opening WhatsApp URL:', whatsappUrl);
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

      // 2. Prepare and send email
      const emailData: EmailFormData & Record<string, unknown> = {
        from_name: formData.name,
        from_email: formData.email,
        to_email: import.meta.env.VITE_EMAILJS_BUSINESS_EMAIL || 'whitepearlfinance@gmail.com',
        phone: formData.phone,
        loan_type: purposeText,
        message: `${whatsappMessage}\n\nAdditional Message: ${formData.message || 'None'}`,
        date: formattedDate,
        time: formData.time,
        company_name: 'White Pearl Finance',
        subject: `New Contact Form Submission from ${formData.name}`,
        to_name: 'Support Team',
        whatsapp_message: whatsappMessage.replace(/\n/g, '<br>')
      };

      console.log('Attempting to send email with data:', emailData);
      const { success, message: emailMessage } = await sendEmail(emailData);
      console.log('Email submission result:', { success, emailMessage });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        purpose: 'loan-inquiry',
        message: '',
        date: '',
        time: ''
      });

      // Close the dialog if open
      setShowMeetingDialog(false);

      // Show success message based on email result
      if (success) {
        toast({
          title: "✅ Request Submitted!",
          description: "We've received your request and sent a confirmation to whitepearlfinance@gmail.com.",
          duration: 5000
        });
      } else {
        console.error('Email submission failed:', emailMessage);
        toast({
          title: "📅 WhatsApp Opened!",
          description: "We've prepared a WhatsApp message for you. Email confirmation may be delayed.",
          duration: 5000
        });
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "❌ Error",
        description: "There was an error processing your request. Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={contentRef} className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-16 pb-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto text-center mb-8 sm:mb-16">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">Contact Us</h1>
        <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
          Have questions or need assistance? Our team is here to help you with all your financial needs.
        </p>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Contact Form */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="p-6">
            <CardTitle className="text-2xl text-white">Send us a Message</CardTitle>
          </CardHeader>
          <CardContent className="pb-6 px-6">
            <form onSubmit={handleScheduleMeeting} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-slate-700/50 border-slate-600 text-white h-11"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-slate-700/50 border-slate-600 text-white h-11"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                  <div className="flex">
                    <div className="flex items-center justify-center px-3 bg-slate-700/50 border border-r-0 border-slate-600 rounded-l-md text-sm text-gray-300">
                      +91
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"

                      placeholder="Your phone number"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purpose" className="text-gray-300">Purpose</Label>
                  <Select
                    value={formData.purpose}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, purpose: value }))}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white h-11">
                      <SelectValue placeholder="Select a purpose" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="loan-inquiry">Loan Inquiry</SelectItem>
                      <SelectItem value="account-support">Account Support</SelectItem>
                      <SelectItem value="general-question">General Question</SelectItem>

                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-gray-300">Preferred Date</Label>
                  <Input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="bg-slate-700/50 border-slate-600 text-white h-11"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-gray-300">Preferred Time</Label>
                  <Input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="bg-slate-700/50 border-slate-600 text-white h-11"
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="message" className="text-gray-300">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-slate-700/50 border-slate-600 text-white min-h-[120px]"
                    placeholder="Type your message here..."
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="p-2 bg-blue-500/10 rounded-lg mt-0.5">
                  <Phone className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Call Us</h3>
                  <a href="tel:+919324471736" className="text-blue-400 hover:underline">+91 93244 71736</a>
                  <p className="text-gray-400 text-sm mt-1">Monday to Saturday, 10AM to 7PM</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-2 bg-green-500/10 rounded-lg mt-0.5">
                  <MessageSquare className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Support</h3>
                  <p className="text-green-400">+91 93244 71736</p>
                  <p className="text-gray-400 text-sm mt-1">Available 24/7</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-2 bg-purple-500/10 rounded-lg mt-0.5">
                  <Mail className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Email Us</h3>
                  <a href="mailto:info@whitepearlfinance.com" className="text-purple-400 hover:underline">info@whitepearlfinance.com</a>
                  <p className="text-gray-400 text-sm mt-1">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-2 bg-amber-500/10 rounded-lg mt-0.5">
                  <MapPin className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Visit Us</h3>
                  <address className="not-italic text-gray-300">
                    10, Ganjawala Apt,<br />
                    Building No. 3,<br />
                    Borivali (W),<br />
                    Mumbai - 400092.</address>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Floating Buttons are now included in App.tsx */}
      <ScheduleMeetingDialog
        open={showMeetingDialog}
        onOpenChange={setShowMeetingDialog}
      />
    </div>
  );
};

export default Contact;
